import { useCallback, useEffect, useRef } from 'react'

import { TimeWindowFieldValue } from '../../../types'

export function useActions(
  value: TimeWindowFieldValue,
  apiChange: (value: TimeWindowFieldValue) => void,
) {
  const fieldValueRef = useRef(value)
  useEffect(() => {
    fieldValueRef.current = value
  }, [value])

  const onDeleteAll = useCallback(() => {
    apiChange({ status: 'exact', value: [] })
  }, [apiChange])

  const onAddTimeWindow = useCallback(() => {
    if (fieldValueRef.current.status === 'mixed') {
      if (process.env.NODE_ENV === 'development') {
        throw new Error('Cannot add a time window on a mixed field')
      }
      return
    }

    const newTimeWindow = { startSec: 9 * 3600, endSec: 18 * 3600 } as uui.domain.rm.TimeWindow

    switch (fieldValueRef.current.value.length) {
      case 0:
        apiChange({ status: 'exact', value: [newTimeWindow] })
        break

      case 1:
        apiChange({ status: 'exact', value: [fieldValueRef.current.value[0], newTimeWindow] })
        break

      case 2:
        if (process.env.NODE_ENV === 'development') {
          throw new Error('Cannot add a new time window, time windows count limit is 2')
        }
        break
    }
  }, [apiChange])

  const onUpdateTimeWindow = useCallback(
    (timeWindow: uui.domain.rm.TimeWindow, timeWindowIndex: 0 | 1) => {
      if (fieldValueRef.current.status === 'mixed') {
        if (process.env.NODE_ENV === 'development') {
          throw new Error('Cannot update a time window on a mixed field')
        }
        return
      }

      const timeWindowTarget = fieldValueRef.current.value[timeWindowIndex]
      if (!timeWindowTarget) {
        if (process.env.NODE_ENV === 'development') {
          throw new Error('Cannot find the time window to update')
        }
        return
      }

      switch (timeWindowIndex) {
        case 0:
          const hasSecondValue = !!fieldValueRef.current.value[1]

          apiChange({
            status: 'exact',
            value: hasSecondValue ? [timeWindow, fieldValueRef.current.value[1]] : [timeWindow],
          })
          break

        case 1:
          if (!fieldValueRef.current.value[0]) {
            if (process.env.NODE_ENV === 'development') {
              throw new Error('Cannot find the time window')
            }
            break
          }

          apiChange({ status: 'exact', value: [fieldValueRef.current.value[0], timeWindow] })
          break
      }
    },
    [apiChange],
  )

  const onRemoveTimeWindow = useCallback(
    (timeWindowIndex: 0 | 1) => {
      if (fieldValueRef.current.status === 'mixed') {
        if (process.env.NODE_ENV === 'development') {
          throw new Error('Cannot update a time window on a mixed field')
        }
        return
      }

      const value = [...fieldValueRef.current.value] as [
        uui.domain.rm.TimeWindow?,
        uui.domain.rm.TimeWindow?,
      ]
      value.splice(timeWindowIndex, 1)
      apiChange({ status: 'exact', value })
    },
    [apiChange],
  )

  return {
    onDeleteAll,
    onAddTimeWindow,
    onUpdateTimeWindow,
    onRemoveTimeWindow,
  }
}
